import { One } from '../../icons/rebalance/One'
import { Two } from '../../icons/rebalance/Two'
import { Three } from '../../icons/rebalance/Three'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import { RebalanceType } from '../../types/rebalance'
interface RebalanceInstructionsProps {
  name: string
  type: RebalanceType
  swpAmountPerMonth?: number
  swpDurationInMonths?: number
  newFundName?: string
  currentFundName?: string
  lumpsumAmount?: number
  highExposureTransferAmount?: number
  isHighExposureFund?: boolean
  missedGains?: number
  currentWealth?: number
}

export const SipAndLumpsumSwpRebalance = ({
  currentFundName,
  newFundName,
  swpAmountPerMonth,
  swpDurationInMonths
}: {
  currentFundName: string
  newFundName: string
  swpAmountPerMonth: number
  swpDurationInMonths: number
}) => {
  return (
    <>
      <div className='flex flex-col gap-4 mb-4'>
        <h2 className='text-13 font-medium text-blue30'>SIP Rebalance</h2>
        <ol className='flex flex-col gap-6'>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <One />
            </span>
            Cancel existing SIP in {currentFundName}
          </li>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <Two />
            </span>
            Start a new SIP in {newFundName} with the same amount
          </li>
        </ol>
      </div>
      <div className='flex flex-col gap-4 mb-4'>
        <h2 className='text-13 font-medium text-blue30'>Lumpsum Rebalance</h2>
        <ol className='flex flex-col gap-6'>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <One />
            </span>
            Withdraw in equal instalments or set up a monthly SWP (Systematic Withdrawal Plan) from {currentFundName}
            <br />
            Amount: {roundOffAndFormatNumberWithCurrency(swpAmountPerMonth)}/month
            <br />
            Duration: {swpDurationInMonths} {swpDurationInMonths === 1 ? 'month' : 'months'}
          </li>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <Two />
            </span>
            Once each instalment or SWP is credited to your bank, reinvest the same amount in {newFundName} as a
            lump-sum
          </li>
        </ol>
        <p className='text-grey40 text-13 font-normal'>Note: SWP can be set up via your broker's app or website.</p>
      </div>
    </>
  )
}

export const SipAndDirectLumpsumRebalance = ({
  currentFundName,
  newFundName,
  lumpsumAmount
}: {
  currentFundName: string
  newFundName: string
  lumpsumAmount: number
}) => (
  <>
    <div className='flex flex-col gap-4 mb-4'>
      <h2 className='text-13 font-medium text-blue30'>SIP Rebalance</h2>
      <ol className='flex flex-col gap-6'>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <One />
          </span>
          Cancel existing SIP in {currentFundName}
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Two />
          </span>
          Start a new SIP in {newFundName} with the same amount
        </li>
      </ol>
    </div>
    <div className='flex flex-col gap-4 mb-4'>
      <h2 className='text-13 font-medium text-blue30'>Lumpsum Rebalance</h2>
      <ol className='flex flex-col gap-6'>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <One />
          </span>
          Redeem {roundOffAndFormatNumberWithCurrency(lumpsumAmount)} from {currentFundName}
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Two />
          </span>
          Wait for the amount to be credited to your bank (usually in 3–4 working days)
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Three />
          </span>
          Re-invest the same amount in {newFundName} as lump-sum
        </li>
      </ol>
    </div>
  </>
)

export const SipOnlyRebalance = ({
  currentFundName,
  newFundName
}: {
  currentFundName: string
  newFundName: string
}) => (
  <div className='flex flex-col gap-4 mb-4'>
    <ol className='flex flex-col gap-6'>
      <li className='flex items-center gap-3'>
        <span className='self-start'>
          <One />
        </span>
        Cancel existing SIP in {currentFundName}
      </li>
      <li className='flex items-center gap-3'>
        <span className='self-start'>
          <Two />
        </span>
        Start a new SIP in {newFundName} with the same amount
      </li>
    </ol>
  </div>
)

export const DirectLumpsumRebalance = ({
  currentFundName,
  lumpsumAmount,
  newFundName,
  highExposureTransferAmount,
  isHighExposureFund,
  missedGains
}: {
  currentFundName: string
  lumpsumAmount: number
  newFundName: string
  highExposureTransferAmount: number
  isHighExposureFund: boolean
  missedGains: number
}) => (
  <>
    <div className='flex flex-col gap-4 mb-4'>
      <h2 className='text-13 font-medium text-blue30'>Lumpsum Rebalance</h2>
      <ol className='flex flex-col gap-6'>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <One />
          </span>
          Redeem {roundOffAndFormatNumberWithCurrency(isHighExposureFund ? highExposureTransferAmount : lumpsumAmount)}{' '}
          from {currentFundName}
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Two />
          </span>
          Wait for the amount to be credited to your bank (usually in 3-4 working days)
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Three />
          </span>
          Re-invest the same amount in {newFundName} as lump-sum
        </li>
      </ol>
    </div>
    {(!isHighExposureFund || (isHighExposureFund && missedGains > 0)) && (
      <div className='flex flex-col gap-4 mb-4'>
        <h2 className='text-13 font-medium text-blue30'>SIP Rebalance</h2>
        <ol className='flex flex-col gap-6'>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <One />
            </span>
            Cancel existing SIP in {currentFundName}
          </li>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <Two />
            </span>
            Start a new SIP in {newFundName} with the same amount
          </li>
        </ol>
      </div>
    )}
  </>
)

export const LumpsumSwpOnlyRebalance = ({
  currentFundName,
  swpAmountPerMonth,
  swpDurationInMonths,
  newFundName,
  highExposureTransferAmount,
  missedGains,
  isHighExposureFund
}: {
  currentFundName: string
  swpAmountPerMonth: number
  swpDurationInMonths: number
  newFundName: string
  highExposureTransferAmount: number
  missedGains: number
  isHighExposureFund: boolean
}) => (
  <>
    <div className='flex flex-col gap-4 mb-4'>
      <ol className='flex flex-col gap-6'>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <One />
          </span>
          <div>
            Withdraw in equal instalments or set up a monthly SWP (Systematic Withdrawal Plan) from {currentFundName}
            <br />
            Amount: {roundOffAndFormatNumberWithCurrency(swpAmountPerMonth)}/month
            <br />
            Duration: {swpDurationInMonths} {swpDurationInMonths === 1 ? 'month' : 'months'}
            <br />
            <br />
            <span className='text-grey40 text-13 font-normal flex-nowrap'>
              Note: Fund value can change with market movements, you may need to adjust the SWP amount or duration if
              needed
            </span>
          </div>
        </li>
        <li className='flex items-center gap-3'>
          <span className='self-start'>
            <Two />
          </span>
          Once each instalment or SWP is credited to your bank, reinvest the same amount in {newFundName} as lump-sum
        </li>
      </ol>
      <p className='text-grey40 text-13 font-normal'>Note: SWP can be set up via your broker's app or website.</p>
    </div>
    {(!isHighExposureFund || (isHighExposureFund && missedGains > 0)) && (
      <div className='flex flex-col gap-4 mb-4'>
        <h2 className='text-13 font-medium text-blue30'>SIP Rebalance</h2>
        <ol className='flex flex-col gap-6'>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <One />
            </span>
            Cancel existing SIP in {currentFundName}
          </li>
          <li className='flex items-center gap-3'>
            <span className='self-start'>
              <Two />
            </span>
            Start a new SIP in {newFundName} with the same amount
          </li>
        </ol>
      </div>
    )}
  </>
)

export const RebalanceInstructions = ({
  missedGains,
  currentFundName,
  type,
  swpAmountPerMonth,
  swpDurationInMonths,
  newFundName,
  highExposureTransferAmount,
  currentWealth,
  isHighExposureFund
}: RebalanceInstructionsProps) => {
  switch (type) {
    case RebalanceType.SIP:
    case RebalanceType.SIP_ELSS:
      return <SipOnlyRebalance currentFundName={currentFundName!} newFundName={newFundName!} />

    case RebalanceType.SWP:
    case RebalanceType.SWP_ELSS:
      return (
        <LumpsumSwpOnlyRebalance
          currentFundName={currentFundName!}
          swpAmountPerMonth={swpAmountPerMonth ?? 0}
          swpDurationInMonths={swpDurationInMonths ?? 0}
          newFundName={newFundName!}
          highExposureTransferAmount={0}
          missedGains={0}
          isHighExposureFund={false}
        />
      )

    case RebalanceType.LUMPSUM:
    case RebalanceType.LUMPSUM_ELSS:
      return (
        <DirectLumpsumRebalance
          currentFundName={currentFundName!}
          lumpsumAmount={currentWealth ?? 0}
          newFundName={newFundName!}
          highExposureTransferAmount={0}
          isHighExposureFund={isHighExposureFund ?? false}
          missedGains={0}
        />
      )

    case RebalanceType.LUMPSUM_SIP_ELSS:
    case RebalanceType.LUMPSUM_SIP:
      return (
        <SipAndDirectLumpsumRebalance
          currentFundName={currentFundName!}
          newFundName={newFundName!}
          lumpsumAmount={currentWealth ?? 0}
        />
      )

    case RebalanceType.SWP_SIP_ELSS:
    case RebalanceType.SWP_SIP:
      return (
        <SipAndLumpsumSwpRebalance
          currentFundName={currentFundName!}
          newFundName={newFundName!}
          swpAmountPerMonth={swpAmountPerMonth ?? 0}
          swpDurationInMonths={swpDurationInMonths ?? 0}
        />
      )

    case RebalanceType.LUMPSUM_HIGH_EXPOSURE:
    case RebalanceType.LUMPSUM_HIGH_EXPOSURE_ELSS:
      return (
        <DirectLumpsumRebalance
          missedGains={missedGains ?? 0}
          highExposureTransferAmount={highExposureTransferAmount ?? 0}
          currentFundName={currentFundName!}
          lumpsumAmount={swpAmountPerMonth ?? 0}
          newFundName={newFundName!}
          isHighExposureFund={isHighExposureFund ?? false}
        />
      )
    case RebalanceType.SWP_HIGH_EXPOSURE:
    case RebalanceType.SWP_HIGH_EXPOSURE_ELSS:
      return (
        <LumpsumSwpOnlyRebalance
          missedGains={missedGains ?? 0}
          isHighExposureFund={isHighExposureFund ?? false}
          highExposureTransferAmount={highExposureTransferAmount ?? 0}
          currentFundName={currentFundName!}
          swpAmountPerMonth={swpAmountPerMonth ?? 0}
          swpDurationInMonths={swpDurationInMonths ?? 0}
          newFundName={newFundName!}
        />
      )
    default:
      return null // Or render a default/error state
  }
}
